/*
 * WideWorkspace — a tool's work area takes the free width of a wide screen.
 *
 * Every page sits in a column glued to the sidebar (1200 px container, tool
 * content 900 px). On a 2560 px monitor that leaves ~1100 px (48 %) empty to
 * the right; on 1920 px, ~450 px. For reading width that is right — intro,
 * how-to, FAQ stay at 900. For the surface a person WORKS on (a timeline, a
 * canvas, a stage) it is the difference between seeing a word on the wave and
 * guessing at it: chat 640, «полэкрана не задействовано… дорожку продлить».
 *
 *   WideWorkspace.attach(el, {
 *       when:      () => bool,   // optional: the tool's own «is the editor open»
 *       reserve:   px | (want) => px,  // a side column the tool lays out INSIDE
 *                                      // the wide area: widen only if the main
 *                                      // part still gains minGain
 *       minGain:   px   (160),   // less than this is not worth a layout change
 *       max:       px   (1840),  // past this a work area stops helping
 *       minViewport: px (1280),
 *       fill:      bool (false),  // take the whole band, start edge beside the
 *                                 // sidebar (a board), not in line with the text
 *       gutter:    px   (24),     // kept free at each edge of the band
 *       onChange:  (wide, widthPx) => {}   // redraw canvases measured once
 *   })  →  { sync(), state() }
 *
 * Or declaratively: data-wide-workspace on the element (data-ww-max,
 * data-ww-reserve, data-ww-min-gain, data-ww-min-viewport, data-ww-gutter,
 * data-ww-fill, data-ww-when="<selector>": wide only while that descendant is
 * shown — an editor that appears after a file is picked) — attached on
 * DOMContentLoaded.
 *
 * A side column: a direct child marked data-ww-side goes beside the work while
 * wide (grid: work | clamp(340px, 24 %, 420px)) and back under it when not —
 * settings and export next to a timeline instead of stretched across 2000 px.
 * Its width is the default `reserve`.
 *
 * What it does, and only this: measures the band the main column leaves
 * beside the sidebar, keeps the element's start edge and grows it toward the
 * end (toward the start too where the end has no room — RTL, whose column is
 * glued to the far side), and when it is worth it
 * sets the element's width to that room through CSSOM — so no stylesheet and
 * no CSP question. Ancestors that clip horizontally (overflow-x hidden / clip)
 * are opened while wide and restored after. The element gets class `ww-on`,
 * its parent `ww-host`, and `wide-workspace:change` fires on the element: a
 * tool hangs its wide layout (side column) on those classes and redraws on the
 * event — and a window `resize` is dispatched on every change, because half the
 * canvases on the site redraw only on that (Leaflet's invalidateSize too).
 * Re-measured on window resize and whenever the element or its parent
 * changes size (a hidden editor that opens, the sidebar that folds).
 * ⛔ No scrolling, no position:sticky — width only.
 */
(function () {
    'use strict';
    if (window.WideWorkspace) return;

    var GUTTER = 24;
    var DEFAULTS = { minGain: 160, max: 1840, minViewport: 1280 };
    var items = [];
    var reported = false;

    function num(v, d) {
        var n = parseFloat(v);
        return isFinite(n) ? n : d;
    }
    /* The decision, with no DOM — what the unit test pins.
       g: { rtl, start (the parent's content start edge, px), left/right (the
       band), max, natural (the width it has now), gain, fill, reserve(w) }.
       → { on, want, shift } — shift: how far the start edge moves into the band. */
    function decide(g) {
        var worth = function (w) { return w - (g.reserve ? g.reserve(w) : 0) >= g.natural + g.gain; };
        // first: keep the start edge in line with the text above, grow toward
        // the end only (a board that wants the whole band skips this)
        if (!g.fill) {
            var w0 = Math.floor(Math.min(g.max, g.rtl ? g.start - g.left : g.right - g.start));
            if (worth(w0)) return { on: true, want: w0, shift: 0 };
        }
        // the end has no room — RTL, whose column is glued to the far side: the
        // free band lies between it and the sidebar, so the work area takes the
        // band, its start edge beside the sidebar as the LTR column's is. Never
        // in LTR: there the column hugs the sidebar (or is centred), and moving
        // the start edge pulls a map out of its own card (/map-measure at 1366).
        if (!g.rtl && !g.fill) return { on: false, want: 0, shift: 0 };
        var w = Math.floor(Math.min(g.max, g.right - g.left));
        if (!worth(w)) return { on: false, want: 0, shift: 0 };
        return { on: true, want: w, shift: Math.max(0, g.rtl ? g.right - g.start : g.start - g.left) };
    }

    function contentWidth(p) {
        var cs = getComputedStyle(p);
        return p.clientWidth - num(cs.paddingLeft, 0) - num(cs.paddingRight, 0);
    }

    function attach(el, opts) {
        if (!el) return null;
        for (var i = 0; i < items.length; i++) if (items[i].el === el) return items[i].api;
        opts = opts || {};
        var it = { el: el, host: el.parentElement, opts: opts, on: false, width: 0, shift: 0, opened: [], api: null };
        var SIDE_GAP = 16;
        function sideOf() {
            for (var c = el.firstElementChild; c; c = c.nextElementSibling) if (c.hasAttribute('data-ww-side')) return c;
            return null;
        }
        function sideWidth(want) { return Math.max(340, Math.min(420, Math.round(want * 0.24))); }
        function layoutSide(on, want) {
            var side = sideOf();
            if (!side) return;
            if (on) {
                el.style.display = 'grid';
                el.style.gridTemplateColumns = 'minmax(0, 1fr) ' + sideWidth(want) + 'px';
                el.style.columnGap = SIDE_GAP + 'px';
                el.style.alignItems = 'start';
                // the side spans every row the work has — and the empty rows past
                // them. As `auto` rows those took a share of the side's height
                // each (95 × 1.7 px: 160 px of nothing under every /trim-family
                // workspace); `min-content` keeps them at 0 until the side is the
                // taller column, and then only the side's own overflow goes there
                el.style.gridAutoRows = 'min-content';
                for (var c = el.firstElementChild; c; c = c.nextElementSibling) {
                    c.style.gridColumn = c === side ? '2' : '1';
                    if (c === side) c.style.gridRow = '1 / span 99';
                }
            } else {
                el.style.display = el.style.gridTemplateColumns = el.style.columnGap = el.style.alignItems = el.style.gridAutoRows = '';
                for (var d = el.firstElementChild; d; d = d.nextElementSibling) d.style.gridColumn = d.style.gridRow = '';
            }
        }

        function openClips(on) {
            if (on) {
                it.opened = [];
                for (var p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
                    var ox = getComputedStyle(p).overflowX;
                    if (ox === 'hidden' || ox === 'clip') {
                        it.opened.push({ node: p, prev: p.style.overflowX });
                        p.style.overflowX = 'visible';
                    }
                }
            } else {
                it.opened.forEach(function (o) { o.node.style.overflowX = o.prev; });
                it.opened = [];
            }
        }

        // the band a work area may use: the main column's box (the sidebar is
        // outside it on either side), less a gutter at each edge
        function band() {
            var m = el.closest('.main-content');
            var docW = document.documentElement.clientWidth;
            var r = m ? m.getBoundingClientRect() : { left: 0, right: docW };
            var g = opts.gutter == null ? GUTTER : num(opts.gutter, GUTTER);
            return { left: Math.max(0, r.left) + g, right: Math.min(docW, r.right) - g };
        }

        function sync() {
            var host = it.host || el.parentElement;
            if (!host) return;
            var on = false, want = 0, shift = 0;
            var visible = el.getClientRects().length > 0;
            var enabled = typeof opts.when === 'function' ? !!opts.when() : true;
            var minVp = opts.minViewport || DEFAULTS.minViewport;
            if (visible && enabled && window.innerWidth >= minVp) {
                // measured from the PARENT: its box does not move when we widen
                var hr = host.getBoundingClientRect(), cs = getComputedStyle(host);
                var rtl = cs.direction === 'rtl';
                var start = rtl ? hr.right - num(cs.paddingRight, 0) : hr.left + num(cs.paddingLeft, 0);
                var b = band();
                var d = decide({
                    rtl: rtl, start: start, left: b.left, right: b.right,
                    max: opts.max || DEFAULTS.max,
                    natural: contentWidth(host),
                    gain: opts.minGain == null ? DEFAULTS.minGain : opts.minGain,
                    fill: !!opts.fill,
                    reserve: function (w) {
                        return typeof opts.reserve === 'function' ? num(opts.reserve(w), 0)
                            : (opts.reserve != null ? num(opts.reserve, 0) : (sideOf() ? sideWidth(w) + SIDE_GAP : 0));
                    }
                });
                on = d.on; want = d.want; shift = d.shift;
            }
            if (on === it.on && (!on || (want === it.width && shift === it.shift))) return;
            var flipped = on !== it.on;
            it.on = on;
            it.width = on ? want : 0;
            it.shift = on ? shift : 0;
            if (flipped) openClips(on);
            el.classList.toggle('ww-on', on);
            host.classList.toggle('ww-host', on);
            // how far the area's start edge left the column (RTL, a board): the
            // area's siblings that keep its start edge (the run counter, a note
            // above it) offset by this — aligned to the old column they stood
            // mid-page beside an area that had moved to the sidebar (/ar, 10.10)
            if (on) host.style.setProperty('--ww-shift', shift + 'px');
            else host.style.removeProperty('--ww-shift');
            if (on) {
                el.style.width = want + 'px';
                el.style.maxWidth = 'none';
                // set even when 0: a tool's own margin must not move the box we placed
                el.style.marginInlineStart = (-shift) + 'px';
                el.style.setProperty('--ww-w', want + 'px');
                layoutSide(true, want);
            } else {
                layoutSide(false, 0);
                el.style.width = '';
                el.style.maxWidth = '';
                el.style.marginInlineStart = '';
                el.style.removeProperty('--ww-w');
            }
            try { el.dispatchEvent(new CustomEvent('wide-workspace:change', { detail: { wide: on, width: it.width, flipped: flipped } })); } catch (e) { /* noop */ }
            if (typeof opts.onChange === 'function') {
                try { opts.onChange(on, it.width, flipped); } catch (e) { /* the tool's redraw must not break the layout */ }
            }
            // after layout: canvases that size themselves on window resize redraw —
            // on every width change, not only on/off: a window going 2560 → 1920
            // keeps the area wide, and the tool's own resize handler ran BEFORE we
            // narrowed it (/trim kept a 1364 px bitmap in a 1070 px box). Our own
            // listener answers with a sync that changes nothing — no loop.
            requestAnimationFrame(function () {
                try { window.dispatchEvent(new Event('resize')); } catch (e) { /* noop */ }
            });
            if (on && !reported && window.__ae) {
                reported = true;
                try { window.__ae('feature_use', { f: 'wide_workspace', w: want, vw: window.innerWidth }); } catch (e) { /* noop */ }
            }
        }

        if (window.ResizeObserver) {
            // the parent's width does not depend on ours — no feedback loop; the
            // element's own box appearing (display:none → shown) is the other cue
            try {
                var ro = new ResizeObserver(function () { schedule(); });
                if (it.host) ro.observe(it.host);
                ro.observe(el);
            } catch (e) { /* noop */ }
        }
        it.api = { sync: sync, state: function () { return { wide: it.on, width: it.width }; } };
        items.push(it);
        sync();
        return it.api;
    }

    var raf = 0;
    function schedule() {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = 0; syncAll(); });
    }
    function syncAll() { items.forEach(function (it) { it.api.sync(); }); }
    window.addEventListener('resize', schedule);

    function scan() {
        var nodes = document.querySelectorAll('[data-wide-workspace]');
        for (var i = 0; i < nodes.length; i++) {
            var n = nodes[i];
            var sel = n.getAttribute('data-ww-when');
            attach(n, {
                when: sel ? (function (root, s) {
                    return function () { var m = root.querySelector(s); return !!(m && m.getClientRects().length); };
                })(n, sel) : null,
                max: num(n.getAttribute('data-ww-max'), DEFAULTS.max),
                reserve: n.hasAttribute('data-ww-reserve') ? num(n.getAttribute('data-ww-reserve'), 0) : null,
                minGain: num(n.getAttribute('data-ww-min-gain'), DEFAULTS.minGain),
                minViewport: num(n.getAttribute('data-ww-min-viewport'), DEFAULTS.minViewport),
                gutter: n.hasAttribute('data-ww-gutter') ? num(n.getAttribute('data-ww-gutter'), GUTTER) : null,
                fill: n.hasAttribute('data-ww-fill')
            });
        }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
    else scan();

    window.WideWorkspace = { attach: attach, sync: syncAll, _decide: decide };
})();
